// src/utils/notes/calloutRender.js
//
// Callout (proje notu) THREE render yardımcıları — SAF fonksiyonlar.
// JbzViewer ve editör-önizleme (useCalloutNotes) ortak kullanır → tek kaynak.
// Notlar dünya koordinatında saklanır; her iki bağlam da ortografik kamera
// kullandığı için render birebir uyumludur.

import * as THREE from 'three';

export const CALLOUT_COLOR = 0x00ccff;
export const CALLOUT_CSS = '#00ccff';

// Bir not için paydaş rengini (CSS hex string) çöz; yoksa default callout rengi.
export function noteColorCSS(note) {
  try {
    // Lazy require — circular bağımlılık önlemek için
    const { colorForRoleLabel } = require('../stakeholderColors');
    const c = colorForRoleLabel(note?.authorRole);
    return c || CALLOUT_CSS;
  } catch { return CALLOUT_CSS; }
}

export function noteColorInt(note) {
  const cssC = noteColorCSS(note);
  if (typeof cssC !== 'string') return CALLOUT_COLOR;
  const s = cssC.startsWith('#') ? cssC.slice(1) : cssC;
  const n = parseInt(s, 16);
  return Number.isFinite(n) ? n : CALLOUT_COLOR;
}

export function withAlpha(hex, alpha) {
  const s = hex.startsWith('#') ? hex.slice(1) : hex;
  const n = parseInt(s, 16) || 0;
  const r = (n >> 16) & 0xff, g = (n >> 8) & 0xff, b = n & 0xff;
  return `rgba(${r},${g},${b},${alpha})`;
}

function wrapText(ctx, text, maxW) {
  const words = text.split(' ');
  const lines = [];
  let cur = '';
  for (const w of words) {
    const test = cur ? cur + ' ' + w : w;
    if (ctx.measureText(test).width > maxW && cur) {
      lines.push(cur);
      cur = w;
    } else {
      cur = test;
    }
  }
  if (cur) lines.push(cur);
  return lines.length ? lines : [''];
}

// Base world-width of a callout (fraction of project view height).
// frameWidth is a multiplier on the canvas width — larger frame means the text
// wraps at a wider column and occupies fewer lines. Font size stays constant.
const CALLOUT_BASE_WIDTH_FRACTION = 0.10;
export const CALLOUT_MIN_FRAME_WIDTH = 0.7;
export const CALLOUT_MAX_FRAME_WIDTH = 3.0;

export function makeTextSprite(text, viewH, header, frameWidth = 1, accentCss = CALLOUT_CSS) {
  const S = 4;
  const fs = 12 * S;           // body font (pixel-fixed)
  const hfs = 12 * S;          // header font — same size as body
  const pad = 6 * S;
  const stripH = hfs * 1.55;   // header bar height
  const baseMaxW = 200 * S;
  const maxW = Math.round(baseMaxW * frameWidth);
  const c = document.createElement('canvas');
  const ctx = c.getContext('2d');

  // Measure body lines
  ctx.font = `bold ${fs}px Arial`;
  const bodyLines = wrapText(ctx, text, maxW - pad * 2);
  const lh = fs * 1.3;
  const bodyH = bodyLines.length * lh;

  // Measure header (single line, truncated if too long)
  let headerText = null;
  let headerBandH = 0;
  if (header && header.trim()) {
    ctx.font = `bold ${hfs}px Arial`;
    headerText = header.trim();
    const maxHW = maxW - pad * 2;
    while (ctx.measureText(headerText).width > maxHW && headerText.length > 3) {
      headerText = headerText.slice(0, -2);
    }
    if (headerText.length < header.trim().length) headerText += '…';
    headerBandH = stripH;
  }

  c.width = maxW;
  c.height = bodyH + pad * 2 + headerBandH;

  // Background
  ctx.fillStyle = 'rgba(0,0,0,.8)';
  ctx.fillRect(0, 0, c.width, c.height);

  if (headerText) {
    ctx.fillStyle = withAlpha(accentCss, 0.22);
    ctx.fillRect(0, 0, c.width, headerBandH);
    ctx.strokeStyle = accentCss;
    ctx.lineWidth = 1 * S;
    ctx.beginPath();
    ctx.moveTo(0, headerBandH);
    ctx.lineTo(c.width, headerBandH);
    ctx.stroke();
    ctx.font = `bold ${hfs}px Arial`;
    ctx.fillStyle = '#ffffff';
    ctx.textBaseline = 'middle';
    ctx.fillText(headerText, pad, headerBandH / 2);
  }

  // Outer border
  ctx.strokeStyle = accentCss;
  ctx.lineWidth = 2 * S;
  ctx.strokeRect(0, 0, c.width, c.height);

  // Body text
  ctx.font = `bold ${fs}px Arial`;
  ctx.fillStyle = accentCss;
  ctx.textBaseline = 'top';
  bodyLines.forEach((l, i) => ctx.fillText(l, pad, headerBandH + pad + i * lh));

  // Resize handle (bottom-right triangle) — görsel ipucu
  const handleSize = 10 * S;
  ctx.fillStyle = accentCss;
  ctx.beginPath();
  ctx.moveTo(c.width, c.height);
  ctx.lineTo(c.width - handleSize, c.height);
  ctx.lineTo(c.width, c.height - handleSize);
  ctx.closePath();
  ctx.fill();

  const tex = new THREE.CanvasTexture(c);
  tex.minFilter = THREE.LinearFilter;
  const mat = new THREE.SpriteMaterial({ map: tex, transparent: true, alphaTest: 0.05, depthTest: false });
  const sprite = new THREE.Sprite(mat);
  // World width scales with frameWidth so the rendered text stays the same
  // physical size (font is pixel-fixed in canvas, world-size grows with canvas).
  const sw = viewH * CALLOUT_BASE_WIDTH_FRACTION * frameWidth;
  const sh = sw * (c.height / c.width);
  sprite.scale.set(sw, sh, 1);
  sprite.center.set(0, 1);
  return sprite;
}

// viewH = "proje referans yüksekliği" (scene bounding box height) — kameranın o
// anki zoom'u değil. Böylece callout boyutu projeye göre sabit olur, zoom'la
// birlikte doğal büyür/küçülür.
export function buildCalloutGroup(note, viewH) {
  const g = new THREE.Group();
  g.userData = { _callout: true, id: note.id };
  g.renderOrder = 999;
  const accentCss = noteColorCSS(note);
  const accentInt = noteColorInt(note);
  // leader line
  const lg = new THREE.BufferGeometry();
  lg.setAttribute('position', new THREE.Float32BufferAttribute([
    note.tx, note.ty, 1, note.bx, note.by, 1,
  ], 3));
  const lm = new THREE.LineBasicMaterial({ color: accentInt, depthTest: false });
  const line = new THREE.Line(lg, lm);
  line.renderOrder = 999;
  g.add(line);
  // target marker — sabit 2 birim yarıçap
  const mr = 2;
  const cg = new THREE.CircleGeometry(mr, 16);
  const cm = new THREE.MeshBasicMaterial({ color: accentInt, depthTest: false });
  const circle = new THREE.Mesh(cg, cm);
  circle.position.set(note.tx, note.ty, 1);
  circle.renderOrder = 999;
  g.add(circle);
  // text sprite with author header — format: "1-İrfan Badur — İş Sahibi"
  let header = null;
  if (note.authorName || note._authorIndex) {
    const nameWithIndex = note._authorIndex
      ? `${note._authorIndex}-${note.authorName || 'Bilinmeyen'}`
      : (note.authorName || '');
    const parts = [nameWithIndex];
    if (note.authorRole) parts.push(note.authorRole);
    header = parts.filter(Boolean).join(' — ');
  }
  const rawFW = typeof note.frameWidth === 'number' && note.frameWidth > 0
    ? note.frameWidth
    : (typeof note.scale === 'number' && note.scale > 0 ? note.scale : 1);
  const fw = Math.max(CALLOUT_MIN_FRAME_WIDTH, Math.min(CALLOUT_MAX_FRAME_WIDTH, rawFW));
  const sprite = makeTextSprite(note.text, viewH, header, fw, accentCss);
  sprite.position.set(note.bx, note.by, 1);
  sprite.renderOrder = 999;
  sprite.userData = { _noteId: note.id, _frameWidth: fw, _text: note.text, _header: header };
  g.add(sprite);
  return g;
}

// Ekran → dünya koordinatı. RAYCASTER tabanlı: kameranın nasıl kurulduğundan
// bağımsız çalışır (JbzViewer frustum-pan VE editör OrbitControls position+zoom
// ikisiyle de doğru). z=0 düzlemine ışın atar.
const _ray = new THREE.Raycaster();
const _plane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0);
const _hit = new THREE.Vector3();
export function screenToWorld(e, camera, el) {
  const r = el.getBoundingClientRect();
  const ndc = {
    x: ((e.clientX - r.left) / r.width) * 2 - 1,
    y: -((e.clientY - r.top) / r.height) * 2 + 1,
  };
  _ray.setFromCamera(ndc, camera);
  const ok = _ray.ray.intersectPlane(_plane, _hit);
  if (ok) return { x: _hit.x, y: _hit.y };
  // Fallback: ortografik frustum bounds (kamera zoom'unu da hesaba kat)
  const zoom = camera.zoom || 1;
  const nx = (e.clientX - r.left) / r.width;
  const ny = (e.clientY - r.top) / r.height;
  const cx = (camera.left + camera.right) / 2 + (camera.position?.x || 0);
  const cy = (camera.top + camera.bottom) / 2 + (camera.position?.y || 0);
  const halfW = (camera.right - camera.left) / 2 / zoom;
  const halfH = (camera.top - camera.bottom) / 2 / zoom;
  return { x: cx - halfW + nx * halfW * 2, y: cy + halfH - ny * halfH * 2 };
}

// Sahnenin dünya-uzayı yüksekliği (bounding box) — callout boyutu için sabit
// referans. Zoom'dan bağımsız; hem JbzViewer hem editörde aynı sonucu verir.
export function computeSceneBaseHeight(scene) {
  if (!scene) return 0;
  scene.updateMatrixWorld(true);
  const box = new THREE.Box3();
  let has = false;
  scene.traverse((child) => {
    if (child.userData?._callout) return; // callout'ları ölçüme dahil etme
    if (child.isMesh || child.isLine || child.isLineSegments || child.isLineLoop) {
      box.expandByObject(child);
      has = true;
    }
  });
  if (!has || box.isEmpty()) return 0;
  const size = box.getSize(new THREE.Vector3());
  return size.y || 0;
}
